// Client-side ID generation using crypto.getRandomValues. Matches the
// formats validated server-side in core/src/ids.rs.

const ALPHABET =
  "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";

export function generateBoardId(): string {
  return base62Encode(16, 22);
}

export function generateTaskId(): string {
  return "t_" + base62Encode(9, 12);
}

export function generateClientOpId(): string {
  return base62Encode(6, 8);
}

export function isValidBoardId(s: string): boolean {
  return /^[0-9A-Za-z]{22}$/.test(s);
}

/** Resolve a user- or OS-supplied board reference to a bare board id.
 *  Accepts a bare 22-char id, a web URL (`https://eyes.wagmorelabs.com/<id>`,
 *  any origin — only the last path segment matters), or the desktop
 *  `eyes://board/<id>` custom-scheme link. Returns null if no valid id is
 *  found. Shared by the deep-link handler and the board-switcher modal. */
export function parseBoardRef(raw: string): string | null {
  const s = raw.trim();
  if (!s) return null;
  if (isValidBoardId(s)) return s;
  let candidate = "";
  try {
    const u = new URL(s);
    if (u.protocol === "eyes:") {
      // eyes://board/<id> parses <id> as the path; eyes://<id> as the host.
      candidate = (u.hostname === "board" ? u.pathname : u.pathname || u.hostname)
        .replace(/^\/+/, "");
    } else {
      // http(s)://host/<id> — take the last non-empty path segment.
      const segs = u.pathname.split("/").filter(Boolean);
      candidate = segs[segs.length - 1] ?? "";
    }
  } catch {
    return null;
  }
  return isValidBoardId(candidate) ? candidate : null;
}

function base62Encode(byteLen: number, outLen: number): string {
  const bytes = new Uint8Array(byteLen);
  crypto.getRandomValues(bytes);
  // Treat bytes as a big-endian integer; repeatedly divide by 62 and
  // collect remainders. Matches the Rust generator in core/src/ids.rs.
  const digits = Array.from(bytes);
  const out: string[] = [];
  while (out.length < outLen) {
    let rem = 0;
    let allZero = true;
    for (let i = 0; i < digits.length; i++) {
      const v = (rem << 8) | digits[i]!;
      digits[i] = Math.floor(v / 62);
      rem = v % 62;
      if (digits[i] !== 0) allZero = false;
    }
    out.push(ALPHABET[rem]!);
    if (allZero && rem === 0 && out.length < outLen) {
      while (out.length < outLen) out.push(ALPHABET[0]!);
      break;
    }
  }
  return out.reverse().join("");
}
